import { useState } from 'react'
import useFormProcessor from '@hooks/useFormProcessor'
import { ReportsProp } from '@templates/ReportsButton'
import {
  ACK_WITH_RISK_MAPS,
  ACK_WITH_RISK_MAPS_REPORT,
  DANGEROUS_REGISTRY,
  DANGEROUS_REGISTRY_REPORT,
  POSITIONS_WITHOUT_RISK_MAPS,
  POSITIONS_WITHOUT_RISK_MAPS_REPORT,
  RISK_MANAGEMENT_PLAN,
  RISK_MANAGEMENT_PLAN_REPORT,
  RISK_MANAGEMENT_SYSTEM,
  RISK_MANAGEMENT_SYSTEM_REPORT,
  RISK_MAPS_REPORTS_ARCHIVE,
  RISK_MAPS_REPORTS_ARCHIVE_REPORT,
  SIZ,
  SIZ_REPORT,
} from '@const/reportNames'
import {
  useGetRiskMapsReportsArchiveMutation,
  useGetPositionWithoutRiskMapReportMutation,
  useGetProfRiskManagementSystemReportMutation,
  useGetRiskMapControlMeasurePlanReportMutation,
  useGetRiskMapDangerReportMutation,
  useGetRiskMapEmployeeReviewReportMutation,
  useGetSizReportMutation,
} from '@services/riskMaps'
import { CompanyOption } from '@models/company'
import { DivisionOption } from '@models/division'
import { FilialOption } from '@models/filial'
import { PositionOption } from '@models/position'
import { Option } from '@organisms/Select'
import useMe from '@hooks/useMe'
import { REVIEW_STATUS_OPTIONS, ReviewStatuses } from '@pages/RiskMaps/const'
import { RiskMapOption } from '@models/riskMap'
import useHasJobs from '@hooks/useHasJobs'
import useRiskMapsFiltersModalFields from './useRiskMapsFiltersModalFields'
import { RiskMapReports } from './enums'

const ReviewStatusesToApi = {
  [ReviewStatuses.REVIEWED]: true,
  [ReviewStatuses.NOT_REVIEWED]: false,
  [ReviewStatuses.ALL]: undefined,
}

type FormValues = {
  company?: CompanyOption
  division?: DivisionOption
  filial?: FilialOption
  position?: PositionOption
  reviewedBeforeAt?: string
  reviewStatus?: Option<ReviewStatuses>
  riskMap?: RiskMapOption
  headPosition?: string
  safetyDivision?: string
}

type FormFilters = {
  company: string | undefined
  companyBranch: string | undefined
  branchSubdivision: string | undefined
  jobPosition: string | undefined
  reviewedBeforeAt?: string | undefined
  reviewStatus?: boolean | undefined
  riskMap?: string | undefined
  riskAssessmentJobPosition?: string | undefined
  laborProtectionService?: string | undefined
}

const useRiskMapsReports = (): ReportsProp => {
  const { hasCompany, hasFilial } = useHasJobs()
  const {
    company: meCompany,
    companyBranch: meCompanyBranch,
    divisionCuratorWithOneDivision,
  } = useMe()
  const [selectedReport, setSelectedReport] = useState(
    RiskMapReports.DANGEROUS_REGISTRY
  )

  const methods = useFormProcessor({
    defaultValues: {
      reviewStatus: REVIEW_STATUS_OPTIONS.find(
        ({ value }) => value === ReviewStatuses.ALL
      ),
    },
  })
  const filterModalFields = useRiskMapsFiltersModalFields(
    methods,
    selectedReport
  )

  const [
    getRiskMapsReportsArchiveMutation,
    { isLoading: getRiskMapsReportsArchiveIsLoading },
  ] = useGetRiskMapsReportsArchiveMutation()

  const [
    getPositionWithoutRiskMapReportMutation,
    { isLoading: getPositionWithoutRiskMapReportMutationIsLoading },
  ] = useGetPositionWithoutRiskMapReportMutation()

  const [
    getRiskMapControlMeasurePlanReportMutation,
    { isLoading: getRiskMapControlMeasurePlanReportMutationIsLoading },
  ] = useGetRiskMapControlMeasurePlanReportMutation()

  const [
    getRiskMapDangerReportMutation,
    { isLoading: getRiskMapDangerReportMutationIsLoading },
  ] = useGetRiskMapDangerReportMutation()

  const [
    getRiskMapEmployeeReviewReportMutation,
    { isLoading: getRiskMapEmployeeReviewReportMutationIsLoading },
  ] = useGetRiskMapEmployeeReviewReportMutation()

  const [
    getProfRiskManagementSystemReportMutation,
    { isLoading: getProfRiskManagementSystemReportMutationIsLoadign },
  ] = useGetProfRiskManagementSystemReportMutation()

  const [getSizReportMutation, { isLoading: getSizReportMutationIsLoading }] =
    useGetSizReportMutation()

  const transformFormValues = (form: FormValues): FormFilters => ({
    company: hasCompany ? form.company?.['@id'] : meCompany?.['@id'],
    companyBranch: hasFilial
      ? form.filial?.['@id']
      : meCompanyBranch?.['@id'],
    branchSubdivision:
      form.division?.['@id'] || divisionCuratorWithOneDivision?.['@id'],
    jobPosition: form.position?.['@id'] || form.headPosition,
    riskAssessmentJobPosition: form.headPosition,
    reviewedBeforeAt: form.reviewedBeforeAt,
    reviewStatus: form.reviewStatus?.value
      ? ReviewStatusesToApi[form.reviewStatus?.value]
      : undefined,
    riskMap: form.riskMap?.['@id'],
    laborProtectionService: form.safetyDivision,
  })

  return {
    transformFormValues,
    onSelectReport: setSelectedReport,
    filterModalFields,
    methods,
    items: [
      {
        title: RISK_MAPS_REPORTS_ARCHIVE,
        isLoading: getRiskMapsReportsArchiveIsLoading,
        onClick: getRiskMapsReportsArchiveMutation,
        id: RISK_MAPS_REPORTS_ARCHIVE_REPORT,
      },
      {
        title: DANGEROUS_REGISTRY,
        isLoading: getRiskMapDangerReportMutationIsLoading,
        onClick: getRiskMapDangerReportMutation,
        id: DANGEROUS_REGISTRY_REPORT,
      },
      {
        title: RISK_MANAGEMENT_PLAN,
        isLoading: getRiskMapControlMeasurePlanReportMutationIsLoading,
        onClick: getRiskMapControlMeasurePlanReportMutation,
        id: RISK_MANAGEMENT_PLAN_REPORT,
      },
      {
        title: ACK_WITH_RISK_MAPS,
        isLoading: getRiskMapEmployeeReviewReportMutationIsLoading,
        onClick: getRiskMapEmployeeReviewReportMutation,
        id: ACK_WITH_RISK_MAPS_REPORT,
      },
      {
        title: POSITIONS_WITHOUT_RISK_MAPS,
        isLoading: getPositionWithoutRiskMapReportMutationIsLoading,
        onClick: getPositionWithoutRiskMapReportMutation,
        id: POSITIONS_WITHOUT_RISK_MAPS_REPORT,
      },
      {
        title: RISK_MANAGEMENT_SYSTEM,
        isLoading: getProfRiskManagementSystemReportMutationIsLoadign,
        onClick: getProfRiskManagementSystemReportMutation,
        id: RISK_MANAGEMENT_SYSTEM_REPORT,
      },
      {
        title: SIZ,
        isLoading: getSizReportMutationIsLoading,
        onClick: getSizReportMutation,
        id: SIZ_REPORT,
      },
    ],
  }
}

export default useRiskMapsReports
